import React, { type FC, useEffect, useState } from 'react';
import _ from 'lodash';
import { translate } from '#packages/i18n';
import constants from '#packages/constants';
import * as util from '#packages/util';
import { topBarMenuOpen, topBarMenuItemHover } from '@wix/bi-logger-editor/v2';

import withSendBi from '../hocs/withSendBi';
import withMenuItems from '../hocs/withMenuItems/withMenuItems';

import withDelayedOpenDropDownBehavior from './behaviors/withDelayedOpenDropDownBehavior';
import withActiveItemBehavior from './behaviors/withActiveItemBehavior';
import withKeyboardBehavior from './behaviors/withKeyboardBehavior';

import IllustratedListPanel from '../dropPanel/illustratedListPanel';
import MenuBarDropDownOnHoverItem from './menuBarOnHoverDropDownItem';
import MenuBarDropDownOnClickItem from './menuBarOnClickDropDownItem';
import SaleIndicator from '../menuBar/saleIndicator/saleIndicator';
import { delay } from './menuBar.utils';
import { mapStateToProps, mapDispatchToProps } from './menuBar.mapper';

import type { MenuBarProps } from './menuBar.types';
import MenuBarItem from '../menuBar/item';

const _delay = delay();
const TIME_IN_WHAT_WE_DECLARE_THAT_USER_SAW_SALE_TOOLTIP = 3000;

export const MenuBar: FC<MenuBarProps> = (props) => {
  const currentSaleId = props.saleEndDate ? props.saleEndDate.toString() : null;
  const [isEndOfSaleInCurrentSession, setIsEndOfSaleInCurrentSession] =
    useState(false);
  const [lastActiveShownSaleSession, setLastActiveShownSaleSession] = useState<
    string | undefined
  >(undefined);
  const [
    couldChangeLocallyLastActiveShownSaleSession,
    setCouldChangeLocallyLastActiveShownSaleSession,
  ] = useState(true);

  useEffect(() => {
    if (!props.sitePreferencesFetchDone || !props.saleEndDate) return;

    const timeUntilTheEndOfTheSale = props.saleEndDate - Date.now();
    const timeoutId = setTimeout(() => {
      setIsEndOfSaleInCurrentSession(true);
    }, timeUntilTheEndOfTheSale);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [props.saleEndDate, props.sitePreferencesFetchDone]);

  useEffect(() => {
    if (couldChangeLocallyLastActiveShownSaleSession) {
      setLastActiveShownSaleSession(props.lastActiveShownSaleSession);
    }
  }, [
    props.lastActiveShownSaleSession,
    couldChangeLocallyLastActiveShownSaleSession,
  ]);

  const isOnClickInsteadOfHover = (itemValue: string) => {
    return Object.values<string>(
      constants.ROOT_COMPS.TOPBAR.MENU_BAR_CLICK_ITEMS,
    ).includes(itemValue);
  };

  const shouldShowSaleIndicator =
    props.sitePreferencesFetchDone &&
    props.isSalePeriod &&
    props.personalSaleCampaignDiscountValue !== null &&
    currentSaleId &&
    currentSaleId !== lastActiveShownSaleSession &&
    !isEndOfSaleInCurrentSession;

  const renderPanelContent = (item: AnyFixMe) => {
    const subItems = item.getListItems && item.getListItems();
    if (subItems) {
      return (
        <IllustratedListPanel
          menuKey={item.key}
          sendBi={props.sendBi}
          automationId={item.dropPanelAutomationId}
          isChecklist={item.isChecklist}
          closePanel={() => props.closeDropPanel(true)}
          items={subItems}
          initialSelectedItem={props.initialSelectedSubItem}
          hasDesignPermissions={props.hasDesignPermissions}
        />
      );
    }

    if (item.DropPanel) {
      return item.DropPanel({
        menuKey: item.key,
        sendBi: props.sendBi,
      });
    }

    return null;
  };

  const setUserSawSaleTooltipInCurrentSaleWithDelay = () => {
    _delay.set(() => {
      if (currentSaleId) {
        setCouldChangeLocallyLastActiveShownSaleSession(false);
        props.setLastActiveShownSaleSession(currentSaleId);
      }
    }, TIME_IN_WHAT_WE_DECLARE_THAT_USER_SAW_SALE_TOOLTIP);
  };

  const abortSetUserSawSaleTooltipInCurrentSale = () => {
    _delay.abort();
  };

  const sendBiOnHover = (key: string) => {
    util.biLogger.report(
      topBarMenuItemHover({
        menu_name: key,
      }),
    );
  };

  const { type, menuItems, getActiveItemIndex } = props;
  return (
    <div
      data-aid="top-bar-menu-bar"
      className={util.cx('top-bar-menu-bar-wrapper', {
        'with-separator': type === 'right' && !!menuItems.length,
      })}
    >
      {menuItems.map((item, itemIndex) => {
        const isActive = getActiveItemIndex() === itemIndex;
        const isOpenOnClick = isOnClickInsteadOfHover(item.key);
        const prefix =
          item.key === 'UPGRADE' && shouldShowSaleIndicator ? (
            <SaleIndicator
              isActive={isActive}
              personalSaleCampaignDiscountValue={
                props.personalSaleCampaignDiscountValue
              }
            />
          ) : null;

        const sharedDropDownProps = {
          key: item.key,
          handlerKey: item.key,
          title: translate(item.title),
          prefix,
          className: item.className,
          automationId: item.automationId,
          getCounter: item.getCounter,
          hasNotification: item.hasNotification,
          isActive,
          openedDropPanel: props.openedDropPanel,
          openDropPanel: (key: string, timeout?: number) => {
            props.openDropPanel(key, timeout);
            setUserSawSaleTooltipInCurrentSaleWithDelay();
          },
          closeDropPanel: (force: boolean) => {
            props.closeDropPanel(force);
            abortSetUserSawSaleTooltipInCurrentSale();
          },
          delayedFadeOut: props.delayedFadeOut,
          dropPanelKey: item.key,
          dropPanelContent: renderPanelContent(item),
          panelClassName: item.panelClassName,
          onClick: item.onClick,
        };

        const dropDownElem = () => {
          if (isOpenOnClick) {
            if (item.customMenuItemComponent) {
              return (
                <item.customMenuItemComponent>
                  <MenuBarItem
                    {...sharedDropDownProps}
                    className={util.cx(
                      item.className,
                      'top-bar-menu-bar-clickable-item',
                    )}
                    onClick={() => {
                      if (!item.isDirectAction) {
                        util.biLogger.report(
                          topBarMenuOpen({
                            menu_name: item.key,
                          }),
                        );
                      }
                    }}
                    onMouseEnter={() => {
                      sendBiOnHover(item.key);
                    }}
                    onMouseLeave={item.onMouseLeave}
                  />
                </item.customMenuItemComponent>
              );
            }

            return (
              <MenuBarDropDownOnClickItem
                {...sharedDropDownProps}
                className={util.cx(
                  item.className,
                  'top-bar-menu-bar-clickable-item',
                )}
                onClick={() => {
                  util.biLogger.report(
                    topBarMenuOpen({
                      menu_name: item.key,
                    }),
                  );
                }}
                onMouseEnter={() => {
                  sendBiOnHover(item.key);
                }}
                delayedFadeOut={false}
              />
            );
          }

          return (
            <MenuBarDropDownOnHoverItem
              {...sharedDropDownProps}
              onMouseEnter={() => {
                sendBiOnHover(item.key);
                if (item.onMouseEnter) {
                  item.onMouseEnter(!!prefix);
                }
              }}
              onMouseLeave={item.onMouseLeave}
            />
          );
        };

        return dropDownElem();
      })}
    </div>
  );
};

const MenuBarWithBehaviors = _.flow(
  util.hoc.connect(
    util.hoc.STORES.EDITOR_API,
    mapStateToProps,
    mapDispatchToProps,
  ),
  withKeyboardBehavior,
  withActiveItemBehavior,
  withDelayedOpenDropDownBehavior,
  withSendBi,
  withMenuItems,
)(MenuBar);

export default MenuBarWithBehaviors;
